import { useEffect, useMemo, useState } from 'react';

const DISMISS_KEY = 'pwaInstallBannerDismissedAt';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days (keeps it from reappearing every load)

function isProbablyIos() {
  // iPadOS can report as MacIntel with touch points.
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const isAppleMobile = /iPad|iPhone|iPod/.test(ua) || /iPad|iPhone|iPod/.test(platform);
  const isIpadOs = platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return isAppleMobile || isIpadOs;
}

function isStandaloneMode() {
  // Install detection differs by platform:
  // - Most browsers expose the display mode via matchMedia.
  // - iOS exposes `navigator.standalone` when launched from home screen.
  const standaloneMatch = window.matchMedia?.('(display-mode: standalone)')?.matches;
  const iosStandalone = typeof navigator !== 'undefined' && 'standalone' in navigator && navigator.standalone;
  return Boolean(standaloneMatch || iosStandalone);
}

function readDismissedAt() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return null;
    const num = Number(raw);
    return Number.isFinite(num) ? num : null;
  } catch {
    return null;
  }
}

function writeDismissedAt(ts) {
  try {
    localStorage.setItem(DISMISS_KEY, String(ts));
  } catch {
    // ignore (private mode / storage disabled)
  }
}

/**
 * Computes "should we show install UI?" in a lightweight, readable way.
 *
 * iOS browsers (Safari/Chrome/Firefox/etc.) do not support `beforeinstallprompt`, so we show
 * manual install guidance instead ("Share → Add to Home Screen").
 * Other supported browsers can show a native install prompt when `beforeinstallprompt` is captured.
 */
export function usePwaInstallContext({ isInstallPromptAvailable, hasInstalledEvent }) {
  const [standalone, setStandalone] = useState(false);
  const [dismissedAt, setDismissedAt] = useState(() => readDismissedAt());

  useEffect(() => {
    const update = () => setStandalone(isStandaloneMode());
    update();

    // Keep state in sync if display-mode changes (rare, but cheap).
    const mql = window.matchMedia?.('(display-mode: standalone)');
    if (!mql) return undefined;

    // Safari < 14 uses addListener/removeListener.
    const onChange = () => update();
    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', onChange);
    else if (typeof mql.addListener === 'function') mql.addListener(onChange);

    return () => {
      if (typeof mql.removeEventListener === 'function') mql.removeEventListener('change', onChange);
      else if (typeof mql.removeListener === 'function') mql.removeListener(onChange);
    };
  }, []);

  // If the app becomes installed, clear banner dismissal so it won't matter later.
  useEffect(() => {
    if (hasInstalledEvent) {
      setDismissedAt(null);
      try {
        localStorage.removeItem(DISMISS_KEY);
      } catch {
        // ignore
      }
    }
  }, [hasInstalledEvent]);

  const isIos = useMemo(() => isProbablyIos(), []);

  const isDismissed = useMemo(() => {
    if (!dismissedAt) return false;
    return Date.now() - dismissedAt < DISMISS_TTL_MS;
  }, [dismissedAt]);

  const canShowBanner = useMemo(() => {
    if (standalone) return false;
    if (hasInstalledEvent) return false;
    if (isDismissed) return false;
    return Boolean(isInstallPromptAvailable || isIos);
  }, [standalone, hasInstalledEvent, isDismissed, isInstallPromptAvailable, isIos]);

  const bannerVariant = useMemo(() => {
    if (!canShowBanner) return 'none';
    if (isInstallPromptAvailable) return 'prompt';
    if (isIos) return 'ios';
    return 'none';
  }, [canShowBanner, isInstallPromptAvailable, isIos]);

  const dismiss = () => {
    const ts = Date.now();
    setDismissedAt(ts);
    writeDismissedAt(ts);
  };

  return {
    // detection
    isStandalone: standalone,
    isIos,
    isInstallPromptAvailable,
    // banner
    canShowBanner,
    bannerVariant, // 'prompt' | 'ios' | 'none'
    dismiss,
  };
}

